import type { ToastItem } from './useToasts.js';

interface ToastRegionProps {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}

function ToastList({
  toasts,
  onDismiss,
}: {
  toasts: ToastItem[];
  onDismiss: (id: number) => void;
}) {
  return (
    <>
      {toasts.map((t) => (
        <div key={t.id} className={`toast ${t.kind}`}>
          <span>{t.message}</span>
          <button
            type="button"
            className="toast__dismiss"
            aria-label="Dismiss notification"
            onClick={() => onDismiss(t.id)}
          >
            ×
          </button>
        </div>
      ))}
    </>
  );
}

/**
 * Canonical toast region: the shared `.toast` styles paired with the useToasts()
 * hook. Extracted from five copies (see
 * docs/solutions/developer-experience/toast-region-duplicated-across-panes-extract-shared-2026-07-02.md).
 *
 * Two live regions, not one. Success and warning toasts report something that
 * already happened and can wait for a pause, so they stay polite. An error toast
 * means something did not happen and the screen still needs attention — a
 * partially-failed bulk approve leaves rows selected and unapproved — so it
 * announces assertively rather than queueing behind whatever is being read.
 *
 * BOTH REGIONS AND THE WRAPPER RENDER UNCONDITIONALLY, EVEN WHEN EMPTY. A live
 * region has to be in the DOM before its content changes for a screen reader to
 * observe the mutation; one inserted already-populated is announced unreliably
 * (NVDA and VoiceOver both drop it). Returning null on an empty list therefore
 * silenced the *first* toast — usually the only one — which is precisely the case
 * that matters. That fix arrived on main for the single-region version; splitting
 * into two regions reintroduces the same trap once per region, so neither may be
 * mounted on demand either. `.toast-region` is position:fixed with
 * pointer-events:none, so an empty one is invisible and inert.
 */
export function ToastRegion({ toasts, onDismiss }: ToastRegionProps) {
  const alerts = toasts.filter((t) => t.kind === 'error');
  const polite = toasts.filter((t) => t.kind !== 'error');
  return (
    <div className="toast-region">
      <div role="status" aria-live="polite" aria-atomic="false">
        <ToastList toasts={polite} onDismiss={onDismiss} />
      </div>
      {/* `aria-live="assertive"` rather than `role="alert"`. The two announce
          identically, but role="alert" is also a queryable landmark, and this region
          is now permanently mounted on every page that renders a toast host — so
          claiming the role put a second, empty "alert" into the accessibility tree of
          screens that have their own (the timesheet grid's per-cell validation errors,
          for one) and made "the alert" ambiguous for AT and tests alike. A bare
          assertive live region is the right primitive for a persistent container. */}
      <div aria-live="assertive" aria-atomic="false" className="toast-region__assertive">
        <ToastList toasts={alerts} onDismiss={onDismiss} />
      </div>
    </div>
  );
}
